import React, { useState } from 'react';
import styled, { css } from 'styled-components';
import { Button } from '@progress/kendo-react-buttons';

export type ButtonProps = {
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void; // to handle onClick functions
  name?: string; // make the component able to receive children elements
  buttonType?:
    | 'view'
    | 'save'
    | 'addSubmit'
    | 'uploadDocBlue'
    | 'uploadDocBlack'
    | 'greyHoverBtn'
    | 'blackHoverBtn'
    | 'delete'
    | 'clearbtn'
    | 'blackBtn'
    | 'addInstallMent';
  //  styling options (you can create as many as you want)
  disabled?: boolean; // make the button disabled or not
  icon?: string;
  className?: string;
  id?: string;
  permissionId?: string;
  svgIcon?: React.ReactNode;
  onBlur?: React.FocusEventHandler<HTMLButtonElement>;
  selected?: boolean;
  loading?: boolean;
  hoverSvgColor?: string;
};
//Reusable ref component
const CustomActionButton = React.forwardRef<Button, ButtonProps>(
  (
    {
      name,
      className,
      id,
      disabled,
      buttonType,
      svgIcon,
      selected,
      onClick,
      onBlur,
      loading,
      hoverSvgColor,
    }: ButtonProps,
    ref,
  ) => {
    const [isButtonHovered, setIsButtonHovered] = useState(false); // Hover state for the button

    return (
      <WrapperDiv
        className={`${className ?? ''} ${selected ? 'selected' : ''}`}
        id={id}
      >
        <Container
          buttonType={buttonType}
          onBlur={onBlur}
          ref={ref} // Forward the ref to the inner button element
          onClick={onClick}
          className={buttonType}
          disabled={disabled}
          onMouseEnter={() => !disabled && setIsButtonHovered(true)} // Set hover state on enter if not disabled
          onMouseLeave={() => setIsButtonHovered(false)} // Reset hover state on leave
        >
          {svgIcon ? (
            <IconWrapper
              hoverSvgColor={hoverSvgColor}
              isHovered={isButtonHovered && !disabled} // Apply hover effect only if not disabled
            >
              {svgIcon ? svgIcon : ''}
            </IconWrapper>
          ) : (
            ''
          )}{' '}
          {loading ? (
            <span className="loader"></span>
          ) : (
            <span
              style={svgIcon ? { marginLeft: '5px' } : { marginLeft: '0px' }}
            >
              {name}
            </span>
          )}
        </Container>
      </WrapperDiv>
    );
  },
);

export default CustomActionButton;
const WrapperDiv = styled.div`
  .loader {
    width: 25px;
    height: 25px;
    border: 3.5px solid ${p => p.theme.white};
    border-bottom-color: transparent;
    border-radius: 50%;
    display: inline-block;
    box-sizing: border-box;
    animation: rotation 1s linear infinite;
  }
  &.disabled-styles {
    button {
      &:disabled {
        pointer-events: none;
        cursor: not-allowed !important;
      }
    }
  }
  &.form-next-button,
  &.form-prev-button {
    button {
      &:hover {
        background-color: rgb(42 42 42) !important;
      }
    }
  }

  @keyframes rotation {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
  button {
    height: 31px;
    font-size: 12px;
    font-weight: 400;
    box-shadow: none;
    border-radius: 8px;
    min-width: 70px;
    display: flex;
    align-items: center;
    justify-content: center;
    white-space: nowrap;
    cursor: pointer;
    .k-button-text {
      :has(svg) {
        display: flex;
        align-items: center;
      }
    }
    &:disabled {
      cursor: not-allowed;
      opacity: 0.6;
    }
  }
`;
export const Container = styled(Button)<ButtonProps>`
  &.k-button {
    ${props => props.buttonType && BUTTONTYPE[props.buttonType]}
  }
  &:focus {
    box-shadow: none;
    outline: 0;
  }
  &.k-button {
    ${props => props.buttonType && BUTTONTYPE[props.buttonType]}
    ${props =>
      props.disabled &&
      css`
        pointer-events: none;
        cursor: not-allowed;
        opacity: 0.6;
      `}
  }

  &:focus {
    box-shadow: none;
    outline: 0;
  }

  &:hover {
    ${props =>
      props.disabled &&
      css`
        background-color: none !important;
        color: none !important;
      `}
  }
`;
const BUTTONTYPE = {
  view: css`
    background: ${p => p.theme.white} !important;
    border: 0.5px solid ${p => p.theme.viewBtnBg} !important;
    color: ${p => p.theme.black} !important;
    height: 26px;
    padding: 0 10px;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 600;
    padding: 0 15px;
    width: auto;
    font-family: 'Open Sans';
    &:hover {
      background: ${p => p.theme.viewbtnHoverClr} !important;
      color: ${p => p.theme.black}!important;
    }
    &:focus {
      box-shadow: none;
    }
  `,
  save: css`
    color: ${p => p.theme.white};
    height: 40px;
    border: 1px solid ${p => p.theme.saveBtnBg} !important;
    font-weight: 600;
    background-color: ${p => p.theme.saveBtnBg} !important;
    border-radius: 5px;
    font-size: 14px;
    padding: 0 15px;
    width: auto;
    font-family: 'Open Sans';
    &:hover {
      background: ${p => p.theme.saveBtnHoverBg} !important;
      border: 1px solid ${p => p.theme.saveBtnHoverBorderColr} !important;
      color: ${p => p.theme.white} !important;
    }
    &:focus {
      box-shadow: none;
    }
  `,
  addSubmit: css`
    height: 39px;
    color: ${p => p.theme.white};
    border: none !important;
    font-weight: 600;
    background-color: ${p => p.theme.addSubmitBtnBG} !important;
    border-radius: 4px;
    font-size: 18px;
    padding: 7px 29px;
    width: auto;
    font-family: 'Open Sans';
    &:hover {
      background: ${p => p.theme.saveBtnHoverBg} !important;
      border: none !important;
      color: ${p => p.theme.white} !important;
    }
    &:focus {
      box-shadow: none;
    }
  `,
  uploadDocBlue: css`
    height: 26px;
    color: ${p => p.theme.white};
    border: none !important;
    font-weight: 400;
    background-color: ${p => p.theme.addSubmitBtnBG} !important;
    border-radius: 4px;
    font-size: 12px;
    padding: 7px 25px;
    width: auto;
    font-family: 'Open Sans';
    &:hover {
      background: ${p => p.theme.saveBtnHoverBg} !important;
      border: none !important;
      color: ${p => p.theme.white} !important;
      font-weight: 600;
    }
    &:focus {
      box-shadow: none;
    }
  `,

  uploadDocBlack: css`
    height: 39px;
    color: ${p => p.theme.black};
    border: 1px solid ${p => p.theme.uploadBlackBtnBorderColr} !important;
    font-weight: 600;
    background-color: ${p => p.theme.uploadBlackBtnBg}!important;
    border-radius: 4px;
    font-size: 12px;
    padding: 0 15px;
    width: auto;
    &:hover {
      background: ${p => p.theme.black} !important;
      border: 1px solid ${p => p.theme.uploadBlackBtnBorderColr} !important;
      color: ${p => p.theme.white} !important;
    }
    &:focus {
      box-shadow: none;
    }
  `,

  greyHoverBtn: css`
    height: 26px;
    color: ${p => p.theme.greyBtnTextClr};
    border: 1px solid ${p => p.theme.UploadDocBoderClr} !important;
    font-weight: 400;
    background-color: ${p => p.theme.white} !important;
    border-radius: 4px;
    font-size: 12px;
    padding: 0 15px;
    width: auto;
    font-family: 'Open Sans';
    &:hover {
      background: ${p => p.theme.exportBtnHoverBg} !important;
      border: 1px solid ${p => p.theme.exportBtnHoverBg} !important;
      color: ${p => p.theme.white} !important;
    }
    &:focus {
      box-shadow: none;
    }
  `,
  blackHoverBtn: css`
    height: 100%;
    min-height: 26px;
    color: ${p => p.theme.black};
    border: 1px solid ${p => p.theme.black} !important;
    font-weight: 600;
    background-color: ${p => p.theme.blackHoverBtnBg} !important;
    border-radius: 4px;
    font-size: 12px;
    padding: 0 15px;
    width: auto;
    &:hover {
      background: ${p => p.theme.black} !important;
      border: 1px solid ${p => p.theme.white} !important;
      color: ${p => p.theme.white} !important;
    }
    &:focus {
      box-shadow: none;
    }
  `,
  blackBtn: css`
    height: 35px;
    color: ${p => p.theme.white};
    border: 1px solid ${p => p.theme.black} !important;
    font-weight: 600;
    background-color: ${p => p.theme.black} !important;
    border-radius: 4px;
    font-size: 12px;
    padding: 0 28px;
    width: auto;
    font-family: 'Open Sans';
    &:hover {
      background: ${p => p.theme.blackButtonHoverColor} !important;
      border: 1px solid ${p => p.theme.blackButtonHoverColor} !important;
      color: ${p => p.theme.white} !important;
    }
    &:focus {
      box-shadow: none;
    }
  `,
  delete: css`
    height: 26px;
    color: ${p => p.theme.deleteBg};
    border: 1px solid ${p => p.theme.deleteBg} !important;
    font-weight: 600;
    background-color: ${p => p.theme.blackHoverBtnBg} !important;
    border-radius: 4px;
    font-size: 12px;
    padding: 4px 16px;
    width: auto;
    font-family: 'Open Sans';
    &:hover {
      background: ${p => p.theme.deleteHoverBg} !important;
      border: 1px solid ${p => p.theme.deleteHoverBorderClr} !important;
      color: ${p => p.theme.white} !important;
    }
    &:focus {
      box-shadow: none;
    }
  `,
  clearbtn: css`
    height: 40px;
    color: ${p => p.theme.clearBtnClr};
    border: 1px solid ${p => p.theme.uploadBlackBtnBorderColr} !important;
    font-weight: 600;
    background-color: ${p => p.theme.uploadBlackBtnBg}!important;
    border-radius: 5px;
    font-size: 14px;
    padding: 10px 20px;
    font-family: 'Open Sans';
    width: auto;
    &:hover {
      background: ${p => p.theme.uploadBlackBtnBorderColr} !important;
      border: 1px solid ${p => p.theme.uploadBlackBtnBorderColr} !important;
      color: ${p => p.theme.white} !important;
    }
    &:focus {
      box-shadow: none;
    }
  `,
  addInstallMent: css`
    height: 40px;
    color: ${p => p.theme.addInstallmentBtnText};
    border: 1px solid ${p => p.theme.addInstallmentBtnBorder} !important;
    font-weight: 600;
    background-color: ${p => p.theme.addInstallmentBtnBg}!important;
    border-radius: 5px;
    font-size: 14px;
    padding: 10px 20px;
    font-family: 'Open Sans';
    width: auto;
    &:hover {
      background: ${p => p.theme.uploadBlackBtnBorderColr} !important;
      border: 1px solid ${p => p.theme.uploadBlackBtnBorderColr} !important;
      color: ${p => p.theme.white} !important;
    }
    &:focus {
      box-shadow: none;
    }
  `,
};

const IconWrapper = styled.div<{ hoverSvgColor?: string; isHovered: boolean }>`
  padding: 0 10px 0 0px;
  svg {
    transition: fill 0.3s;
  }
  img {
    width: 22px;
    height: 22px;
  }

  svg path {
    fill: ${props =>
      props.isHovered && props.hoverSvgColor
        ? props.hoverSvgColor
        : 'currentColor'};
  }
`;
